import { AlertTriangle, Check } from 'lucide-react';
import type { ComponentType } from 'react';

import { Badge } from '@/components/ui/badge';

import type { CodingJobStatus } from '../types';

interface JobStatusMeta {
  label: string;
  variant: 'outline' | 'secondary';
  icon?: ComponentType<{ className?: string }>;
  iconClassName?: string;
}

/**
 * Status ladder map (design §11.2): `pending → provisioned → indexed →
 * pr-open → done`, any stage can fail. Visual weight follows importance (A4):
 * the two states awaiting a human (`pr-open`, `done`) use the filled
 * `secondary` badge; in-flight states stay `outline`. `failed` is marked by
 * the red icon ONLY — the badge text stays `foreground` (P1 §3.8, B1/B4), so
 * no new colors are introduced (H1).
 */
const JOB_STATUS_META: Record<CodingJobStatus, JobStatusMeta> = {
  pending: { label: 'Pending', variant: 'outline' },
  provisioned: { label: 'Provisioned', variant: 'outline' },
  indexed: { label: 'Indexed', variant: 'outline' },
  'pr-open': { label: 'PR open', variant: 'secondary' },
  done: { label: 'Done', variant: 'secondary', icon: Check },
  failed: {
    label: 'Failed',
    variant: 'outline',
    icon: AlertTriangle,
    iconClassName: 'text-destructive',
  },
};

export interface JobStatusBadgeProps {
  status: CodingJobStatus;
}

/** Text label always renders — status is never conveyed by color alone (B1). */
export function JobStatusBadge({ status }: JobStatusBadgeProps) {
  const meta = JOB_STATUS_META[status] ?? { label: status, variant: 'outline' };
  const Icon = meta.icon;

  return (
    <Badge variant={meta.variant} className="gap-1.5">
      {Icon ? (
        <Icon className={meta.iconClassName ?? 'h-3 w-3 text-foreground'} aria-hidden="true" />
      ) : null}
      {meta.label}
    </Badge>
  );
}
